import { useEffect, useRef, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { Sparkles, X, Maximize2, RotateCcw } from 'lucide-react';
import { useLabels } from '../../config/useLabels';
import { useBrandProfile } from '../../contexts/brand-profile';
import { useGenieChat } from '../../contexts/genie-chat';
import { brandProfileOf } from '../../config/brandProfiles';
import { brandCssVars } from '../../config/brandTheme';
import { GenieConversation } from './GenieConversation';

/** Pages the bubble stays off: the assistant itself, and checkout, where it would sit on the pay button. */
function hiddenOn(pathname: string): boolean {
  return pathname === '/assistant' || pathname.startsWith('/payment-checkout');
}

/**
 * The floating Assistant bubble, on every parent page except those `hiddenOn` lists.
 *
 * Tapping it opens the Parent Genie over the current page — a bottom sheet on a phone, a
 * card in the corner on a wider screen — sharing the conversation with /assistant. It never
 * speaks up on its own: a one-time per-page question beside it was tried and was too noisy.
 *
 * Rendered through a portal to <body>: the parent Layout sits inside a pull-to-refresh
 * container that translates its children while pulling and listens for touches on them, so
 * a fixed panel inside it would move with a pull and scrolling it could trigger a refresh.
 */
export function GenieBubble({ isMobile }: { isMobile: boolean }) {
  const t = useLabels();
  const { activeBrand } = useBrandProfile();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { messages, loading, clearThread, panelOpen, openPanel, closePanel } = useGenieChat();

  // On a phone the sheet covers the page, so following a link out of an answer should
  // reveal where it went. On a wider screen the panel sits beside the page and can stay.
  const lastPathRef = useRef(pathname);
  useEffect(() => {
    if (lastPathRef.current === pathname) return;
    lastPathRef.current = pathname;
    if (isMobile && panelOpen) closePanel();
  }, [pathname, isMobile, panelOpen, closePanel]);

  // Keep the page behind the sheet from scrolling on a phone; Escape closes the panel.
  useEffect(() => {
    if (!panelOpen) return;
    const previousOverflow = document.body.style.overflow;
    if (isMobile) document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') closePanel(); };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKey);
    };
  }, [panelOpen, isMobile, closePanel]);

  if (hiddenOn(pathname)) return null;

  // Sit above the bottom nav (and the home indicator) on a phone.
  const anchorStyle = isMobile ? { bottom: 'calc(5.5rem + env(safe-area-inset-bottom, 0px))' } : undefined;
  const anchorClass = isMobile ? 'right-4' : 'right-6 bottom-6';

  function openFullPage() {
    closePanel();
    navigate('/assistant');
  }

  // Outside the Layout's own div, so the brand's tokens and font are installed again here.
  return createPortal(
    <div style={{ ...brandCssVars(activeBrand), fontFamily: 'var(--brand-font)' } as CSSProperties}>
      {!panelOpen && (
        <button
          type="button"
          onClick={() => openPanel('bubble')}
          aria-label={t('nav.assistant')}
          data-testid="genie-bubble"
          style={anchorStyle}
          className={`fixed z-40 ${anchorClass} h-14 w-14 rounded-full bg-gradient-to-br from-blue-600 to-indigo-600 text-white shadow-lg flex items-center justify-center hover:shadow-xl active:scale-95 transition`}
        >
          <Sparkles className="h-6 w-6" />
        </button>
      )}

      {panelOpen && (
        <>
          {isMobile && (
            <div className="fixed inset-0 z-[60] bg-black/30" onClick={closePanel} aria-hidden="true" />
          )}
          <div
            role="dialog"
            aria-modal={isMobile}
            aria-label={t('nav.assistant')}
            data-testid="genie-panel"
            className={
              isMobile
                ? 'fixed inset-x-0 bottom-0 z-[61] h-[85dvh] flex flex-col bg-white rounded-t-2xl shadow-2xl pb-safe'
                : 'fixed right-6 bottom-6 z-[61] w-[400px] h-[min(640px,calc(100dvh-3rem))] flex flex-col bg-white rounded-2xl shadow-2xl border border-gray-200'
            }
          >
            <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-100">
              <Sparkles className="h-5 w-5 text-blue-600" />
              <span className="font-semibold text-gray-900">{t('nav.assistant')}</span>
              <div className="ml-auto flex items-center gap-1">
                {messages.length > 0 && (
                  <button
                    type="button"
                    onClick={clearThread}
                    disabled={loading}
                    aria-label={t('genie.newChat')}
                    title={t('genie.newChat')}
                    className="p-2 rounded-full text-gray-500 hover:text-gray-800 hover:bg-gray-100 disabled:opacity-50"
                  >
                    <RotateCcw className="h-4 w-4" />
                  </button>
                )}
                <button
                  type="button"
                  onClick={openFullPage}
                  aria-label={t('genie.openFullPage')}
                  title={t('genie.openFullPage')}
                  className="p-2 rounded-full text-gray-500 hover:text-gray-800 hover:bg-gray-100"
                >
                  <Maximize2 className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={closePanel}
                  aria-label={t('genie.close')}
                  title={t('genie.close')}
                  className="p-2 rounded-full text-gray-500 hover:text-gray-800 hover:bg-gray-100"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
            {messages.length === 0 && (
              <div className="px-4 pt-3">
                <p className="text-sm font-medium text-gray-900">{t('genie.title')}</p>
                {brandProfileOf(activeBrand).bilingual && <p className="text-xs text-gray-500">{t('genie.subtitle')}</p>}
              </div>
            )}
            <GenieConversation variant="panel" />
          </div>
        </>
      )}
    </div>,
    document.body,
  );
}
